import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { Box, Tabs, Tab } from "@mui/material";
import MetricMonthView from "./MetricsMonthViews/MetricMonthGraphView/MetricMonthView";
import MetricMonthTableView from "./MetricsMonthViews/MetricMonthTableView/MetricMonthTableView";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { forecastersEndPointLogic } from "../../../../../../logic/AssetManagement/Forecasters/forecastersEndPointLogic";
import {
  setDisplayErrorSnackBar,
  setUserToken,
} from "../../../../../../features/userSlice";
import CircularProgress from "@mui/material/CircularProgress";
import store from "../../../../../../store";
import { useSearchParams } from "react-router-dom";

interface Props {
  open: boolean;
  handleClose: () => void;
  selectedSiteFromParent?: string; // Optional prop to pass selectedSite from parent
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function MetricsByMonthDialogue({ open, handleClose }: Props) {
  const [value, setValue] = React.useState(0);

  // Reset to graph view (tab index 0) whenever the dialog opens
  useEffect(() => {
    if (open) {
      setValue(0);
    }
  }, [open]);

  const handleChange = (_: unknown, newValue: number) => {
    setValue(newValue);
  };

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  const token = useSelector(setUserToken);
  const [searchParams] = useSearchParams();
  const oauthTokenHeader = token?.payload?.user?.userToken;
  const selectedMetric = useSelector(
    (state: any) => state.forecasters?.selectedMetric
  );
  const selectedMonth = useSelector(
    (state: any) => state.forecasters?.selectedMonth
  );
  const selectedSite = searchParams.get("selectedSite") as any;

  const QUERY_ID = "5665673409724416";
  const QUERY_ID_LAST_YEAR = "5743670015819776";
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchForcastGraphAndTableData = async () => {
      if (open && selectedMetric && selectedSite) {
        try {
          setLoading(true);
          Promise.all([
            await forecastersEndPointLogic.getForecasterGraphAndTableData(
              oauthTokenHeader,
              QUERY_ID,
              selectedMonth,
              selectedSite,
              selectedMetric,
              dispatch
            ),
            await forecastersEndPointLogic.getForecasterLastYearData(
              oauthTokenHeader,
              QUERY_ID_LAST_YEAR,
              selectedMonth,
              selectedSite,
              selectedMetric,
              dispatch
            ),
          ]);
        } catch (error: any) {
          store.dispatch(
            setDisplayErrorSnackBar({
              message: "SomeThing went wrong.",
              type: "error",
            })
          );
        } finally {
          setLoading(false);
        }
      }
    };

    if (selectedMetric && selectedSite) {
      fetchForcastGraphAndTableData();
    }
  }, [
    selectedMetric,
    selectedMonth,
    oauthTokenHeader,
    selectedSite,
    dispatch,
    open,
  ]);

  const formatMetricName = (metric: string) => {
    return metric
      ?.split("_")
      ?.join(" ")
      ?.replace(/\b\w/g, (char) => char.toUpperCase());
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{formatMetricName(selectedMetric)} By Month</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <>
            <Tabs
              sx={{ borderBottom: 1, borderColor: "divider" }}
              value={value}
              onChange={handleChange}
              aria-label="basic tabs example"
              textColor="secondary"
              indicatorColor="secondary"
            >
              <Tab label="Graph view" {...a11yProps(0)} />
              <Tab label="Table view" {...a11yProps(1)} />
            </Tabs>
            <CustomTabPanel value={value} index={0}>
              <MetricMonthView />
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
              <MetricMonthTableView />
            </CustomTabPanel>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: "20px" }}>
        <Button onClick={handleClose} color="secondary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
